import type { SubjectTypeDefinitionsFetchFailedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeDefinitionsFetchFailed';
import FetchSubjectTypeDefinitions from '@Application/SubjectTypeDefinitions/Queries/FetchSubjectTypeDefinitions';
import fetchSubjectTypeDefinitions from '@Application/SubjectTypeDefinitions/fetchSubjectTypeDefinitions';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import BeneficiaryTypeCard from '@UI/Pages/Administration/SubjectTypes/SubjectTypeList/BeneficiaryTypeCard';
import NewSubjectTypeButton from '@UI/Pages/Administration/SubjectTypes/SubjectTypeList/NewSubjectTypeButton';
import SubjectTypeCard from '@UI/Pages/Administration/SubjectTypes/SubjectTypeList/SubjectTypeCard';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import { FolderCog } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

const SKELETON_KEYS = ['first', 'second', 'third', 'fourth'] as const;
const DEFAULT_LOAD_ERROR_MESSAGE = 'Une erreur est survenue pendant le chargement des types de dossier.';

const gridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(24rem, 100%), 1fr));
	gap: 1.25rem;
`;

/**
 * The beneficiary system type first, then the account's dossier types: active ones first then archived ones. The list is refetched after every
 * mutation: the store empties it on archive, restore and save.
 */
const SubjectTypeListContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	// Kept apart from `useStoreQuery`'s own `error`, which loses `payload.message`.
	const [fetchErrorMessage, setFetchErrorMessage] = React.useState<string | null>(null);

	const {
		data: definitions,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.definitions,
		stopOn: (value) => !!value && value.accountId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchSubjectTypeDefinitions(new FetchSubjectTypeDefinitions(accountId))).catch(
				(action: SubjectTypeDefinitionsFetchFailedAction) => {
					setFetchErrorMessage(action.payload?.message ?? null);
					throw action;
				},
			);
		},
		deps: [accountId],
	});

	const orderedTypes = React.useMemo(() => {
		const types = definitions?.list ?? [];

		return [...types.filter((type) => !type.isArchived), ...types.filter((type) => type.isArchived)];
	}, [definitions]);

	if (definitions === null && error === null) {
		return (
			<div css={gridStyle}>
				{SKELETON_KEYS.map((key) => (
					<Skeleton key={key} height="9rem" borderRadius={theme.radii.xlarge} animation="wave" />
				))}
			</div>
		);
	}

	if (definitions === null) {
		return (
			<ErrorState
				title="Impossible de charger les types de dossier"
				description={fetchErrorMessage ?? DEFAULT_LOAD_ERROR_MESSAGE}
				onRetry={refetch}
			/>
		);
	}

	return (
		<React.Fragment>
			<div css={gridStyle}>
				<BeneficiaryTypeCard />
				{orderedTypes.map((subjectType) => (
					<SubjectTypeCard key={subjectType.id} subjectType={subjectType} />
				))}
			</div>
			<ConditionalLoader data={orderedTypes.length === 0}>
				<EmptyState
					title="Aucun type de dossier pour l'instant."
					icon={<FolderCog size={28} />}
					action={<NewSubjectTypeButton />}
				/>
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default SubjectTypeListContent;
